import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
} from '@mui/icons-material';
import api from '../../utils/api';
import { en } from '../../translations/en';

const SearchTab: React.FC = () => {
  const [reindexing, setReindexing] = useState(false);
  const [reindexSuccess, setReindexSuccess] = useState<string | null>(null);
  const [reindexError, setReindexError] = useState<string | null>(null);
  const [reindexResults, setReindexResults] = useState<{
    items_processed: number;
    refreshed: number;
    missing: number;
    errors: number;
  } | null>(null);
  const [reindexStatusMessage, setReindexStatusMessage] = useState<string | null>(null);
  const [reindexProgress, setReindexProgress] = useState<{
    started_at?: string;
    parallel_segments?: number | null;
    segments_total?: number;
    segments_complete_count?: number;
    items_processed_live?: number;
    per_segment?: Record<string, number>;
    per_segment_complete?: Record<string, boolean>;
  } | null>(null);
  const [reindexConfirmDialogOpen, setReindexConfirmDialogOpen] = useState(false);
  const reindexPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopReindexPolling = useCallback(() => {
    if (reindexPollRef.current) {
      clearInterval(reindexPollRef.current);
      reindexPollRef.current = null;
    }
  }, []);

  // Clean up polling on unmount
  useEffect(() => {
    return () => stopReindexPolling();
  }, [stopReindexPolling]);

  const pollReindexStatus = useCallback(() => {
    setReindexStatusMessage('Reindexing in progress...');

    reindexPollRef.current = setInterval(async () => {
      try {
        const statusResponse = await api.get('/api/admin/search/reindex/status');
        const {
          status,
          results,
          message,
          error,
          started_at,
          parallel_segments,
          segments_total,
          segments_complete_count,
          items_processed_live,
          per_segment,
          per_segment_complete,
        } = statusResponse.data;

        // Snapshot live progress fields (populated by the service as it works).
        // A completed reindex won't have per_segment anymore, so we only update
        // when there's something to show.
        if (started_at || per_segment) {
          setReindexProgress({
            started_at,
            parallel_segments,
            segments_total,
            segments_complete_count,
            items_processed_live,
            per_segment,
            per_segment_complete,
          });
        }

        if (status === 'completed') {
          stopReindexPolling();
          setReindexing(false);
          setReindexStatusMessage(null);
          setReindexProgress(null);
          setReindexSuccess(message || en.admin.search.reindexSuccess);
          if (results) {
            setReindexResults(results);
          }
        } else if (status === 'failed') {
          stopReindexPolling();
          setReindexing(false);
          setReindexStatusMessage(null);
          setReindexProgress(null);
          setReindexError(error || en.admin.search.reindexError);
        }
        // status === 'running': keep polling
      } catch {
        // Transient error — keep polling, the cluster may be busy
      }
    }, 3000);
  }, [stopReindexPolling]);

  const handleConfirmReindex = async () => {
    setReindexConfirmDialogOpen(false);
    setReindexing(true);
    setReindexSuccess(null);
    setReindexError(null);
    setReindexResults(null);
    setReindexStatusMessage(null);
    setReindexProgress(null);

    try {
      await api.post('/api/admin/search/reindex');
      pollReindexStatus();
    } catch (_err: unknown) {
      setReindexing(false);
      setReindexError(en.admin.search.reindexError);
    }
  };

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {en.admin.search.maintenanceTitle}
        </Typography>

        {/* Reindex Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6">{en.admin.search.title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {en.admin.search.description}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setReindexConfirmDialogOpen(true)}
            disabled={reindexing}
            startIcon={reindexing ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
          >
            {reindexing ? en.admin.search.reindexing : en.admin.search.reindexAll}
          </Button>
        </Box>

        {reindexSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setReindexSuccess(null)}>
            {reindexSuccess}
          </Alert>
        )}

        {reindexError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setReindexError(null)}>
            {reindexError}
          </Alert>
        )}

        {reindexing && (
          <Card variant="outlined" sx={{ mb: 2 }} data-testid="reindex-progress">
            <CardContent>
              {(() => {
                const itemsLive = reindexProgress?.items_processed_live ?? 0;
                const segmentsTotal = reindexProgress?.segments_total ?? 0;
                const segmentsDone = reindexProgress?.segments_complete_count ?? 0;
                const startedAt = reindexProgress?.started_at
                  ? new Date(reindexProgress.started_at).getTime()
                  : null;
                // eslint-disable-next-line react-hooks/purity
                const elapsedMs = startedAt ? Date.now() - startedAt : 0;
                const elapsedSec = Math.max(1, Math.floor(elapsedMs / 1000));
                const mm = String(Math.floor(elapsedSec / 60)).padStart(2, '0');
                const ss = String(elapsedSec % 60).padStart(2, '0');
                const rate = itemsLive > 0 ? itemsLive / elapsedSec : 0;
                const progressPct =
                  segmentsTotal > 0 ? (segmentsDone / segmentsTotal) * 100 : undefined;

                return (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {reindexStatusMessage || 'Reindexing in progress...'}
                      </Typography>
                      {reindexProgress?.parallel_segments != null && (
                        <Chip
                          label={`parallel_segments = ${reindexProgress.parallel_segments}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    <LinearProgress
                      variant={progressPct != null ? 'determinate' : 'indeterminate'}
                      value={progressPct}
                      sx={{ mb: 1.5 }}
                    />

                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Items processed
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {itemsLive.toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Rate
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {rate.toFixed(1)} items/sec
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Elapsed
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {mm}:{ss}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Segments complete
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {segmentsTotal > 0 ? `${segmentsDone} / ${segmentsTotal}` : '—'}
                        </Typography>
                      </Grid>
                    </Grid>

                    {reindexProgress?.per_segment &&
                      Object.keys(reindexProgress.per_segment).length > 1 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                          {Object.entries(reindexProgress.per_segment).map(([seg, count]) => {
                            const isComplete =
                              reindexProgress.per_segment_complete?.[seg] === true;
                            return (
                              <Chip
                                key={seg}
                                label={`Seg ${seg}: ${count.toLocaleString()}`}
                                color={isComplete ? 'success' : 'default'}
                                variant={isComplete ? 'filled' : 'outlined'}
                                size="small"
                              />
                            );
                          })}
                        </Box>
                      )}
                  </>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {reindexResults && !reindexing && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {en.admin.search.itemsProcessed}
                  </Typography>
                  <Typography variant="h6">{reindexResults.items_processed.toLocaleString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {en.admin.search.refreshed}
                  </Typography>
                  <Typography variant="h6">{reindexResults.refreshed.toLocaleString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {en.admin.search.missing}
                  </Typography>
                  <Typography variant="h6">{reindexResults.missing.toLocaleString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {en.admin.search.errors}
                  </Typography>
                  <Typography variant="h6" color={reindexResults.errors > 0 ? 'error' : 'inherit'}>
                    {reindexResults.errors.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Search Reindex Confirmation Dialog */}
      <Dialog
        open={reindexConfirmDialogOpen}
        onClose={() => setReindexConfirmDialogOpen(false)}
      >
        <DialogTitle>{en.admin.search.reindexAll}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {en.admin.search.confirmReindex}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReindexConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmReindex} variant="contained" color="primary">
            {en.admin.search.reindexAll}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SearchTab;
